/**
 * Touch-based D-pad controls as a fallback when gyroscope is unavailable.
 * Renders a semi-transparent directional pad overlay and processes
 * touch events to determine Pac-Man's direction.
 */
import Phaser from 'phaser';

type Direction = 'left' | 'right' | 'up' | 'down' | 'none';

/** D-pad sizing and positioning constants. */
const DPAD_SIZE = 120;
const BUTTON_SIZE = 36;
const DPAD_MARGIN = 16;
const DPAD_ALPHA = 0.35;
const DPAD_ACTIVE_ALPHA = 0.7;

export class TouchInput {
  private currentDirection: Direction = 'none';
  private readonly scene: Phaser.Scene;
  private readonly container: Phaser.GameObjects.Container;
  private readonly buttons: Map<Direction, Phaser.GameObjects.Graphics> = new Map();
  private activeButton: Direction = 'none';

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.container = scene.add.container(0, 0).setDepth(200).setScrollFactor(0);
    this.createDpad();
    this.setupTouchListeners();
  }

  getDirection(): Direction {
    return this.currentDirection;
  }

  setVisible(visible: boolean): void {
    this.container.setVisible(visible);
  }

  destroy(): void {
    this.scene.input.off('pointerdown');
    this.scene.input.off('pointermove');
    this.scene.input.off('pointerup');
    this.container.destroy();
  }

  private createDpad(): void {
    const gameWidth = this.scene.scale.width;
    const gameHeight = this.scene.scale.height;

    // Position D-pad at bottom-right of the screen
    const centerX = gameWidth - DPAD_SIZE / 2 - DPAD_MARGIN;
    const centerY = gameHeight - DPAD_SIZE / 2 - DPAD_MARGIN;

    this.container.setPosition(centerX, centerY);

    // Draw semi-transparent circular background
    const bg = this.scene.add.graphics();
    bg.fillStyle(0x222222, 0.25);
    bg.fillCircle(0, 0, DPAD_SIZE / 2);
    this.container.add(bg);

    // Create directional buttons
    this.createButton('up', 0, -BUTTON_SIZE);
    this.createButton('down', 0, BUTTON_SIZE);
    this.createButton('left', -BUTTON_SIZE, 0);
    this.createButton('right', BUTTON_SIZE, 0);

    // Draw direction arrows on buttons
    this.drawArrow('up', 0, -BUTTON_SIZE);
    this.drawArrow('down', 0, BUTTON_SIZE);
    this.drawArrow('left', -BUTTON_SIZE, 0);
    this.drawArrow('right', BUTTON_SIZE, 0);
  }

  private createButton(direction: Exclude<Direction, 'none'>, x: number, y: number): void {
    const btn = this.scene.add.graphics();
    btn.fillStyle(0x444444, DPAD_ALPHA);
    btn.fillRoundedRect(
      x - BUTTON_SIZE / 2,
      y - BUTTON_SIZE / 2,
      BUTTON_SIZE,
      BUTTON_SIZE,
      6,
    );
    this.container.add(btn);
    this.buttons.set(direction, btn);
  }

  private drawArrow(direction: Exclude<Direction, 'none'>, x: number, y: number): void {
    const arrow = this.scene.add.graphics();
    arrow.fillStyle(0xffffff, 0.8);

    const s = 8; // arrow half-size

    arrow.beginPath();
    switch (direction) {
      case 'up':
        arrow.moveTo(x, y - s);
        arrow.lineTo(x + s, y + s);
        arrow.lineTo(x - s, y + s);
        break;
      case 'down':
        arrow.moveTo(x, y + s);
        arrow.lineTo(x + s, y - s);
        arrow.lineTo(x - s, y - s);
        break;
      case 'left':
        arrow.moveTo(x - s, y);
        arrow.lineTo(x + s, y - s);
        arrow.lineTo(x + s, y + s);
        break;
      case 'right':
        arrow.moveTo(x + s, y);
        arrow.lineTo(x - s, y - s);
        arrow.lineTo(x - s, y + s);
        break;
    }
    arrow.closePath();
    arrow.fillPath();
    this.container.add(arrow);
  }

  private setupTouchListeners(): void {
    // Use Phaser's input system for touch events on the whole scene
    this.scene.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      this.handleTouch(pointer);
    });

    this.scene.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (pointer.isDown) {
        this.handleTouch(pointer);
      }
    });

    this.scene.input.on('pointerup', () => {
      this.resetHighlight();
    });
  }

  private handleTouch(pointer: Phaser.Input.Pointer): void {
    // Convert pointer position to local D-pad coordinates
    const localX = pointer.x - this.container.x;
    const localY = pointer.y - this.container.y;

    // Check if within D-pad radius
    const dist = Math.sqrt(localX * localX + localY * localY);
    if (dist > DPAD_SIZE / 2 + 10) {
      // Touch is outside D-pad - use swipe-style detection anywhere on screen
      this.handleSwipe(pointer);
      return;
    }

    // Determine direction based on angle from center
    const direction = this.positionToDirection(localX, localY);
    if (direction !== 'none') {
      this.currentDirection = direction;
      this.highlightButton(direction);
    }
  }

  private handleSwipe(pointer: Phaser.Input.Pointer): void {
    // For touches outside the D-pad, use swipe direction
    if (!pointer.isDown) {
      return;
    }

    const dx = pointer.x - pointer.downX;
    const dy = pointer.y - pointer.downY;
    const minSwipeDist = 15;

    if (Math.abs(dx) < minSwipeDist && Math.abs(dy) < minSwipeDist) {
      return;
    }

    if (Math.abs(dx) > Math.abs(dy)) {
      this.currentDirection = dx > 0 ? 'right' : 'left';
    } else {
      this.currentDirection = dy > 0 ? 'down' : 'up';
    }
  }

  private positionToDirection(x: number, y: number): Direction {
    const absX = Math.abs(x);
    const absY = Math.abs(y);
    const minDist = 8; // dead zone in center

    if (absX < minDist && absY < minDist) {
      return 'none';
    }

    if (absX > absY) {
      return x > 0 ? 'right' : 'left';
    }
    return y > 0 ? 'down' : 'up';
  }

  private highlightButton(direction: Direction): void {
    // Reset previous highlight
    this.resetHighlight();

    if (direction === 'none') {
      return;
    }

    const btn = this.buttons.get(direction);
    if (btn) {
      btn.clear();
      btn.fillStyle(0x6666ff, DPAD_ACTIVE_ALPHA);

      const offsets = this.getButtonOffsets(direction);
      btn.fillRoundedRect(
        offsets.x - BUTTON_SIZE / 2,
        offsets.y - BUTTON_SIZE / 2,
        BUTTON_SIZE,
        BUTTON_SIZE,
        6,
      );
      this.activeButton = direction;
    }
  }

  private resetHighlight(): void {
    if (this.activeButton === 'none') {
      return;
    }

    const btn = this.buttons.get(this.activeButton);
    if (btn) {
      btn.clear();
      btn.fillStyle(0x444444, DPAD_ALPHA);

      const offsets = this.getButtonOffsets(this.activeButton);
      btn.fillRoundedRect(
        offsets.x - BUTTON_SIZE / 2,
        offsets.y - BUTTON_SIZE / 2,
        BUTTON_SIZE,
        BUTTON_SIZE,
        6,
      );
    }
    this.activeButton = 'none';
  }

  private getButtonOffsets(direction: Direction): { x: number; y: number } {
    switch (direction) {
      case 'up': return { x: 0, y: -BUTTON_SIZE };
      case 'down': return { x: 0, y: BUTTON_SIZE };
      case 'left': return { x: -BUTTON_SIZE, y: 0 };
      case 'right': return { x: BUTTON_SIZE, y: 0 };
      default: return { x: 0, y: 0 };
    }
  }
}
